import React, { useMemo, useRef, useEffect, useState } from 'react';

interface ChartDataPoint {
  timestamp?: string; // Optional - only present for hourly interval
  date?: string; // Optional - present for hourly/daily intervals
  hour?: number; // Optional - only present for hourly interval
  weekStart?: string; // Optional - only present for weekly interval
  weekEnd?: string; // Optional - only present for weekly interval
  month?: string; // Optional - only present for monthly interval (e.g., "2025-10")
  monthStart?: string; // Optional - only present for monthly interval
  monthEnd?: string; // Optional - only present for monthly interval
  views: number;
  likes: number;
  saves: number;
  shares: number;
}

interface ChartMetadata {
  days: number;
  interval: string;
  dataPoints: number;
}

interface SimpleLineChartProps {
  data: ChartDataPoint[];
  metadata?: ChartMetadata;
  chartType?: 'views' | 'watchTime';
  avgWatchDuration?: number; // in seconds
  videoDuration?: number; // in seconds - from viewerBehavior
}

const SimpleLineChart: React.FC<SimpleLineChartProps> = ({ data, metadata, chartType = 'views', avgWatchDuration = 0, videoDuration = 0 }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(1000);
  const tooltipTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [tooltip, setTooltip] = useState<{
    visible: boolean;
    x: number;
    y: number;
    data: ChartDataPoint | null;
    index: number;
    svgX?: number; // X position in SVG coordinates for vertical line
  }>({ visible: false, x: 0, y: 0, data: null, index: -1, svgX: 0 });

  // Get container width for responsive chart
  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        const width = containerRef.current.offsetWidth;
        // Only update if we have a valid width (greater than 0)
        if (width > 0) {
          setContainerWidth(Math.max(600, width - 48)); // Subtract padding
        }
      }
    };

    let resizeObserver: ResizeObserver | null = null;

    // Initial update with a small delay to ensure container is rendered
    const timeoutId = setTimeout(() => {
      updateWidth();
      
      // Set up ResizeObserver after container is rendered
      if (containerRef.current && typeof ResizeObserver !== 'undefined') {
        resizeObserver = new ResizeObserver(() => {
          updateWidth();
        });
        resizeObserver.observe(containerRef.current);
      }
    }, 100);

    // Also listen to window resize
    window.addEventListener('resize', updateWidth);
    
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', updateWidth);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, [data]); // Re-run when data changes (when tab becomes visible)

  // Cleanup tooltip timeout on unmount
  useEffect(() => {
    return () => {
      if (tooltipTimeoutRef.current) {
        clearTimeout(tooltipTimeoutRef.current);
      }
    };
  }, []);

  const chartData = useMemo(() => {
    if (!data || data.length === 0) return null;

    // Format labels based on interval
    const formatLabel = (point: ChartDataPoint) => {
      if (metadata?.interval === 'hourly' && point.hour !== undefined) {
        return `${point.hour}:00`;
      } else if (metadata?.interval === 'weekly' && point.weekStart && point.weekEnd) {
        // Format week range: "Sep 19 - Sep 25"
        const startDate = new Date(point.weekStart);
        const endDate = new Date(point.weekEnd);
        const startFormatted = startDate.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
        const endFormatted = endDate.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
        return `${startFormatted} - ${endFormatted}`;
      } else if (metadata?.interval === 'monthly' && point.month) {
        // Format monthly: "Oct 2025"
        try {
          const [year, month] = point.month.split('-');
          if (year && month) {
            const date = new Date(parseInt(year), parseInt(month) - 1);
            if (!isNaN(date.getTime())) {
              return date.toLocaleDateString('en-GB', { month: 'short', year: 'numeric' });
            }
          }
          return point.month; // Fallback
        } catch (e) {
          return point.month || ''; // Fallback on error
        }
      } else if (metadata?.interval === 'daily' && point.date) {
        const date = new Date(point.date);
        return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
      }
      return point.date || '';
    };

    return data.map(formatLabel);
  }, [data, metadata]);

  const maxValue = useMemo(() => {
    if (!data || data.length === 0) return 10;
    
    if (chartType === 'watchTime') {
      // Calculate watch time in hours: views * videoDuration / 3600
      // Use videoDuration from viewerBehavior for watch time calculation
      const durationToUse = videoDuration || avgWatchDuration || 0;
      const watchTimeValues = data.map(d => {
        const views = d.views || 0;
        return (views * durationToUse) / 3600;
      });
      const max = Math.max(...watchTimeValues, 0);
      if (max === 0) return 10;
      // For sparse data, use smaller increments
      if (max < 1) return Math.ceil(max * 10) / 10;
      const magnitude = Math.pow(10, Math.floor(Math.log10(max)));
      return Math.ceil(max / magnitude) * magnitude || 10;
    } else {
      // For views chart, use all metrics: views, likes, shares, saves
      const allValues = data.flatMap(d => [
        d.views || 0,
        d.likes || 0,
        d.shares || 0,
        d.saves || 0
      ]);
      const max = Math.max(...allValues, 0);
      if (max === 0) return 10;
      
      // Use "nice numbers" algorithm for Y-axis max
      // This ensures clean, evenly spaced labels
      if (max <= 2) {
        return 2;
      } else if (max <= 5) {
        return 5;
      } else if (max <= 10) {
        return 10;
      } else if (max <= 20) {
        return 20;
      } else if (max <= 50) {
        return 50;
      } else if (max <= 100) {
        return 100;
      } else {
        // For larger values, round to next nice number
        const magnitude = Math.pow(10, Math.floor(Math.log10(max)));
        const normalized = max / magnitude;
        let niceNormalized: number;
        if (normalized <= 1) niceNormalized = 1;
        else if (normalized <= 2) niceNormalized = 2;
        else if (normalized <= 5) niceNormalized = 5;
        else niceNormalized = 10;
        return niceNormalized * magnitude;
      }
    }
  }, [data, chartType, avgWatchDuration, videoDuration]);

  const scaleY = (value: number, height: number) => {
    return height - (value / maxValue) * height;
  };

  const chartHeight = 400;
  const padding = { top: 40, right: 40, bottom: 50, left: 50 }; // Increased bottom padding to accommodate x-axis title
  
  // Calculate chart width - use container width primarily (standard chart behavior)
  const chartWidth = useMemo(() => {
    // Always use container width for standard chart behavior
    // This prevents excessive empty space and makes the chart responsive
    return containerWidth;
  }, [containerWidth]);

  // Generate paths for all lines - memoized to recalculate when chartWidth changes
  // Must be before early return to follow Rules of Hooks
  const chartPaths = useMemo(() => {
    if (!data || data.length === 0) {
      return { viewsPath: '', likesPath: '', sharesPath: '', savesPath: '', watchTimePath: '' };
    }
    const dataLength = data.length;
    const divisor = dataLength > 1 ? dataLength - 1 : 1;
    const chartAreaWidth = chartWidth - padding.left - padding.right;
    
    const viewsPath = data.map((point, index) => {
      const x = padding.left + (index / divisor) * chartAreaWidth;
      const y = padding.top + scaleY(point.views || 0, chartHeight - padding.top - padding.bottom);
      return `${index === 0 ? 'M' : 'L'} ${x} ${y}`;
    }).join(' ');

    const likesPath = data.map((point, index) => {
      const x = padding.left + (index / divisor) * chartAreaWidth;
      const y = padding.top + scaleY(point.likes || 0, chartHeight - padding.top - padding.bottom);
      return `${index === 0 ? 'M' : 'L'} ${x} ${y}`;
    }).join(' ');

    const sharesPath = data.map((point, index) => {
      const x = padding.left + (index / divisor) * chartAreaWidth;
      const y = padding.top + scaleY(point.shares || 0, chartHeight - padding.top - padding.bottom);
      return `${index === 0 ? 'M' : 'L'} ${x} ${y}`;
    }).join(' ');

    const savesPath = data.map((point, index) => {
      const x = padding.left + (index / divisor) * chartAreaWidth;
      const y = padding.top + scaleY(point.saves || 0, chartHeight - padding.top - padding.bottom);
      return `${index === 0 ? 'M' : 'L'} ${x} ${y}`;
    }).join(' ');

    // Watch time path (calculated from views * videoDuration / 3600)
    // Use videoDuration from viewerBehavior for watch time calculation
    const durationToUse = videoDuration || avgWatchDuration || 0;
    const watchTimePath = chartType === 'watchTime' ? data.map((point, index) => {
      const x = padding.left + (index / divisor) * chartAreaWidth;
      const watchTimeHours = ((point.views || 0) * durationToUse) / 3600;
      const y = padding.top + scaleY(watchTimeHours, chartHeight - padding.top - padding.bottom);
      return `${index === 0 ? 'M' : 'L'} ${x} ${y}`;
    }).join(' ') : '';

    return { viewsPath, likesPath, sharesPath, savesPath, watchTimePath };
  }, [data, chartWidth, padding, chartHeight, scaleY, chartType, avgWatchDuration, videoDuration]);

  const { viewsPath, likesPath, sharesPath, savesPath, watchTimePath } = chartPaths;

  // Y-axis labels - use "nice numbers" algorithm for evenly spaced labels
  const yAxisLabels = useMemo(() => {
    const labels = [];
    
    // Calculate a "nice" max value that creates evenly spaced intervals
    const niceMax = (() => {
      if (maxValue <= 0) return 10;
      if (maxValue <= 2) return 2;
      if (maxValue <= 5) return 5;
      if (maxValue <= 10) return 10;
      
      // For larger values, round up to next "nice" number
      const magnitude = Math.pow(10, Math.floor(Math.log10(maxValue)));
      const normalized = maxValue / magnitude;
      let niceNormalized: number;
      
      if (normalized <= 1) niceNormalized = 1;
      else if (normalized <= 2) niceNormalized = 2;
      else if (normalized <= 5) niceNormalized = 5;
      else niceNormalized = 10;
      
      return niceNormalized * magnitude;
    })();
    
    // Determine number of steps based on niceMax
    let yAxisSteps = 5;
    if (niceMax <= 2) {
      yAxisSteps = 2; // 0, 1, 2
    } else if (niceMax <= 5) {
      yAxisSteps = 5; // 0, 1, 2, 3, 4, 5
    } else if (niceMax <= 10) {
      yAxisSteps = 5; // 0, 2, 4, 6, 8, 10
    } else {
      yAxisSteps = 5; // Standard 5 steps
    }
    
    // Calculate step size for evenly spaced values
    const stepSize = niceMax / yAxisSteps;
    
    // Generate evenly spaced labels
    for (let i = 0; i <= yAxisSteps; i++) {
      const value = stepSize * (yAxisSteps - i);
      
      // Round to appropriate precision
      let roundedValue: number;
      if (niceMax <= 2) {
        roundedValue = Math.round(value * 10) / 10;
      } else if (niceMax <= 10) {
        roundedValue = Math.round(value);
      } else {
        // For larger values, round to appropriate precision
        const magnitude = Math.pow(10, Math.floor(Math.log10(niceMax)) - 1);
        roundedValue = Math.round(value / magnitude) * magnitude;
      }
      
      labels.push({
        value: roundedValue,
        y: padding.top + (i / yAxisSteps) * (chartHeight - padding.top - padding.bottom)
      });
    }
    
    return labels;
  }, [maxValue, chartHeight, padding]);

  // X-axis labels - strict spacing algorithm to prevent clustering
  const xAxisLabels = useMemo(() => {
    if (!data || data.length === 0) return [];
    
    const availableWidth = chartWidth - padding.left - padding.right;
    const dataPoints = data.length;
    
    // Minimum spacing between labels (in pixels) - increased to prevent clustering
    let minSpacing = 100; // Default minimum spacing
    if (metadata?.interval === 'hourly') {
      minSpacing = 80; // Hourly labels like "12:00" need less space
    } else if (metadata?.interval === 'daily') {
      minSpacing = 90; // Daily labels like "19 Dec"
    } else if (metadata?.interval === 'weekly') {
      minSpacing = 130; // Weekly labels are longer and rotated - increased for 90 days
    } else if (metadata?.interval === 'monthly') {
      minSpacing = 90; // Monthly labels like "Oct 2025" - shorter, need less space
    }
    
    // Calculate maximum number of labels that can fit without overlap
    const maxLabels = Math.max(2, Math.floor(availableWidth / minSpacing));
    
    // Determine target number of labels based on data points and interval
    let targetLabels: number;
    if (dataPoints <= 3) {
      // For very few data points, show all
      targetLabels = dataPoints;
    } else if (metadata?.interval === 'monthly') {
      // For monthly, always show all labels (typically 3-4 months for 90 days)
      // Monthly labels are short ("Oct 2025") so they can fit easily
      targetLabels = dataPoints;
    } else if (metadata?.interval === 'weekly' && metadata?.days === 90) {
      // For 90 days weekly (~13 weeks), show every 2nd or 3rd week
      targetLabels = Math.min(maxLabels, Math.max(5, Math.ceil(dataPoints / 2)));
    } else if (dataPoints <= 6) {
      // For few data points, show every other
      targetLabels = Math.min(maxLabels, Math.ceil(dataPoints / 2));
    } else if (dataPoints <= 12) {
      // For medium datasets, show approximately every 3rd
      targetLabels = Math.min(maxLabels, Math.max(4, Math.ceil(dataPoints / 3)));
    } else {
      // For large datasets, strictly limit based on available space
      targetLabels = Math.min(maxLabels, Math.max(6, Math.floor(dataPoints / 4)));
    }
    
    // Ensure we don't exceed maxLabels
    targetLabels = Math.min(targetLabels, maxLabels);
    
    // Calculate step size to achieve target labels
    const step = Math.max(1, Math.ceil(dataPoints / targetLabels));
    
    // Build label array ensuring first and last are always included
    const labels: typeof data = [];
    const includedIndices = new Set<number>();
    
    // Always include first
    includedIndices.add(0);
    labels.push(data[0]);
    
    // Add labels at step intervals, but ensure minimum spacing
    let lastIncludedIndex = 0;
    for (let i = step; i < dataPoints - 1; i += step) {
      // Ensure minimum spacing from last included label
      if (i - lastIncludedIndex >= step && !includedIndices.has(i)) {
        includedIndices.add(i);
        labels.push(data[i]);
        lastIncludedIndex = i;
      }
    }
    
    // Always include last if not already included and has enough spacing
    const lastIndex = dataPoints - 1;
    if (!includedIndices.has(lastIndex)) {
      // Check if last label has enough spacing from previous
      if (lastIndex - lastIncludedIndex >= Math.floor(step / 2)) {
        labels.push(data[lastIndex]);
      }
    }
    
    // Sort by original index to maintain order
    return labels.sort((a, b) => {
      const indexA = data.indexOf(a);
      const indexB = data.indexOf(b);
      return indexA - indexB;
    });
  }, [data, chartWidth, padding, metadata?.interval]);

  // Adjust SVG height based on whether labels are rotated and x-axis title
  const hasRotatedLabels = metadata?.interval === 'weekly';
  // Increased height to accommodate x-axis title (35px below labels for non-rotated, 50px for rotated)
  const svgHeight = chartHeight + (hasRotatedLabels ? 60 : 50);

  // Tooltip handlers - for specific data point hover
  const handleMouseMove = (e: React.MouseEvent<SVGElement>, index: number) => {
    // Validate index and data
    if (!containerRef.current || !data || index < 0 || index >= data.length) {
      console.warn('Invalid index or data in handleMouseMove:', { index, dataLength: data?.length });
      return;
    }
    
    // Cancel any pending hide timeout
    if (tooltipTimeoutRef.current) {
      clearTimeout(tooltipTimeoutRef.current);
      tooltipTimeoutRef.current = null;
    }
    
    // Get the SVG element - try ownerSVGElement first, then closest
    const svgElement = (e.currentTarget as SVGElement).ownerSVGElement || 
                       (e.currentTarget.closest('svg') as SVGSVGElement | null);
    if (!svgElement) return;
    
    const svgRect = svgElement.getBoundingClientRect();
    const x = e.clientX - svgRect.left;
    const y = e.clientY - svgRect.top;
    
    // Calculate SVG X position for vertical line - use the exact same calculation as data points
    const divisor = data.length > 1 ? data.length - 1 : 1;
    const chartAreaWidth = chartWidth - padding.left - padding.right;
    const svgX = padding.left + (index / divisor) * chartAreaWidth;
    
    // Get the data point - double check index is valid
    const pointData = data[index];
    if (!pointData) {
      console.warn('No data found at index:', index);
      return;
    }
    
    // Set tooltip with the correct data point
    setTooltip({
      visible: true,
      x: x,
      y: y,
      data: pointData, // Use the data at the specific index
      index: index, // Use the exact index passed
      svgX
    });
  };

  const handleMouseLeave = () => {
    // Add a longer delay before hiding tooltip to allow mouse to move to tooltip
    if (tooltipTimeoutRef.current) {
      clearTimeout(tooltipTimeoutRef.current);
    }
    tooltipTimeoutRef.current = setTimeout(() => {
      setTooltip({ visible: false, x: 0, y: 0, data: null, index: -1, svgX: 0 });
    }, 300); // Increased delay for smoother experience
  };

  const handleTooltipMouseEnter = () => {
    // Cancel hide timeout if mouse enters tooltip
    if (tooltipTimeoutRef.current) {
      clearTimeout(tooltipTimeoutRef.current);
    }
  };

  const handleTooltipMouseLeave = () => {
    // Add delay when leaving tooltip too
    if (tooltipTimeoutRef.current) {
      clearTimeout(tooltipTimeoutRef.current);
    }
    tooltipTimeoutRef.current = setTimeout(() => {
      setTooltip({ visible: false, x: 0, y: 0, data: null, index: -1, svgX: 0 });
    }, 200);
  };

  // Handle mouse move over entire chart area for smoother tracking
  const handleChartMouseMove = (e: React.MouseEvent<SVGSVGElement>) => {
    if (!containerRef.current || !data || data.length === 0) return;
    
    const svgElement = e.currentTarget;
    const svgRect = svgElement.getBoundingClientRect();
    const mouseX = e.clientX - svgRect.left;
    const mouseY = e.clientY - svgRect.top;
    
    // Only process if mouse is within chart area
    if (mouseX < padding.left || mouseX > chartWidth - padding.right) return;
    if (mouseY < padding.top || mouseY > chartHeight - padding.bottom) return;
    
    // Find closest data point by calculating actual X positions
    const chartAreaWidth = chartWidth - padding.left - padding.right;
    const divisor = data.length > 1 ? data.length - 1 : 1;
    
    // Calculate the actual X position for each data point and find the closest one
    let closestIndex = 0;
    let minDistance = Infinity;
    
    for (let i = 0; i < data.length; i++) {
      const dataPointX = padding.left + (i / divisor) * chartAreaWidth;
      const distance = Math.abs(mouseX - dataPointX);
      
      if (distance < minDistance) {
        minDistance = distance;
        closestIndex = i;
      }
    }
    
    // Ensure index is valid
    const clampedIndex = Math.max(0, Math.min(closestIndex, data.length - 1));
    
    // Calculate SVG X position for vertical line (use the actual data point position)
    const svgX = padding.left + (clampedIndex / divisor) * chartAreaWidth;
    
    // Get the correct data point
    const pointData = data[clampedIndex];
    if (!pointData) return;
    
    setTooltip({
      visible: true,
      x: mouseX,
      y: mouseY,
      data: pointData,
      index: clampedIndex,
      svgX
    });
  };

  // Format tooltip label
  const getTooltipLabel = (point: ChartDataPoint): string => {
    if (metadata?.interval === 'hourly' && point.hour !== undefined) {
      return `${point.hour}:00`;
    } else if (metadata?.interval === 'weekly' && point.weekStart && point.weekEnd) {
      const startDate = new Date(point.weekStart);
      const endDate = new Date(point.weekEnd);
      const startFormatted = startDate.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
      const endFormatted = endDate.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
      return `${startFormatted} - ${endFormatted}`;
    } else if (metadata?.interval === 'monthly' && point.month) {
      try {
        const [year, month] = point.month.split('-');
        if (year && month) {
          const date = new Date(parseInt(year), parseInt(month) - 1);
          if (!isNaN(date.getTime())) {
            return date.toLocaleDateString('en-GB', { month: 'short', year: 'numeric' });
          }
        }
        return point.month;
      } catch (e) {
        return point.month || '';
      }
    } else if (point.date) {
      const date = new Date(point.date);
      return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    }
    return 'Unknown';
  };

  // Early return after all hooks (Rules of Hooks compliance)
  if (!data || data.length === 0) {
    return (
      <div className="text-center text-muted p-4">
        <p>No chart data available</p>
      </div>
    );
  }
  
  return (
    <div 
      ref={containerRef}
      className="simple-line-chart" 
      style={{ width: '100%', overflowX: 'auto', minWidth: 0, position: 'relative' }}
      onMouseLeave={handleMouseLeave}
    >
      <div style={{ width: '100%', minWidth: 0 }}>
        <svg 
          width={containerWidth} 
          height={svgHeight} 
          style={{ 
            display: 'block',
            width: '100%',
            maxWidth: '100%',
            minWidth: 0
          }}
          onMouseMove={handleChartMouseMove}
          onMouseLeave={handleMouseLeave}
        >
        {/* Grid lines */}
        {yAxisLabels.map((label, index) => (
          <g key={index}>
            <line
              x1={padding.left}
              y1={label.y}
              x2={chartWidth - padding.right}
              y2={label.y}
              stroke="#e9ecef"
              strokeWidth="1"
            />
          </g>
        ))}

        {/* Y-axis line */}
        <line
          x1={padding.left}
          y1={padding.top}
          x2={padding.left}
          y2={chartHeight - padding.bottom}
          stroke="#dee2e6"
          strokeWidth="1"
        />

        {/* X-axis line */}
        <line
          x1={padding.left}
          y1={chartHeight - padding.bottom}
          x2={chartWidth - padding.right}
          y2={chartHeight - padding.bottom}
          stroke="#dee2e6"
          strokeWidth="1"
        />

        {/* Y-axis title */}
        <text
          x={20}
          y={chartHeight / 2}
          textAnchor="middle"
          fontSize="14"
          fill="#495057"
          fontWeight="600"
          transform={`rotate(-90 ${20} ${chartHeight / 2})`}
        >
          {chartType === 'watchTime' ? 'Watch Time (Hours)' : 'Count'}
        </text>

        {/* Y-axis labels */}
        {yAxisLabels.map((label, index) => (
          <text
            key={index}
            x={padding.left - 15}
            y={label.y + 5}
            textAnchor="end"
            fontSize="13"
            fill="#495057"
            fontWeight="500"
          >
            {chartType === 'watchTime' 
              ? `${label.value.toFixed(0)}hrs`
              : label.value >= 1000 
                ? `${(label.value / 1000).toFixed(1)}K` 
                : label.value % 1 === 0 
                  ? label.value.toFixed(0) 
                  : label.value.toFixed(1)}
          </text>
        ))}

        {/* X-axis labels */}
        {xAxisLabels.map((point, index) => {
          const dataIndex = data.indexOf(point);
          const divisor = data.length > 1 ? data.length - 1 : 1;
          const chartAreaWidth = chartWidth - padding.left - padding.right;
          const x = padding.left + (dataIndex / divisor) * chartAreaWidth;
          let label = '';
          let shouldRotate = false;
          
          if (metadata?.interval === 'hourly' && point.hour !== undefined) {
            label = `${point.hour}:00`;
          } else if (metadata?.interval === 'weekly' && point.weekStart && point.weekEnd) {
            // Format week range: Use shorter format "19 Sep" or rotate
            const startDate = new Date(point.weekStart);
            const endDate = new Date(point.weekEnd);
            // Use shorter format: "19 Sep - 25 Sep" or just start date if too long
            const startFormatted = startDate.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
            const endFormatted = endDate.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
            // Only show full range if months are same, otherwise shorten
            if (startDate.getMonth() === endDate.getMonth()) {
              label = `${startFormatted.split(' ')[0]} - ${endFormatted}`;
            } else {
              label = `${startFormatted} - ${endFormatted}`;
            }
            shouldRotate = true; // Rotate weekly labels to prevent overlap
          } else if (metadata?.interval === 'monthly' && point.month) {
            // Format monthly: "Oct 2025"
            try {
              const [year, month] = point.month.split('-');
              if (year && month) {
                const date = new Date(parseInt(year), parseInt(month) - 1);
                if (!isNaN(date.getTime())) {
                  label = date.toLocaleDateString('en-GB', { month: 'short', year: 'numeric' });
                } else {
                  // Fallback if date parsing fails
                  label = point.month;
                }
              } else {
                label = point.month;
              }
            } catch (e) {
              // Fallback if formatting fails
              label = point.month || '';
            }
          } else if (metadata?.interval === 'daily' && point.date) {
            const date = new Date(point.date);
            label = date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
          } else if (point.date) {
            const date = new Date(point.date);
            label = date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
          }
          
          return (
            <text
              key={dataIndex}
              x={x}
              y={chartHeight - padding.bottom + (shouldRotate ? 35 : 20)}
              textAnchor={shouldRotate ? "end" : "middle"}
              fontSize="11"
              fill="#495057"
              fontWeight="500"
              transform={shouldRotate ? `rotate(-45 ${x} ${chartHeight - padding.bottom + 35})` : ''}
            >
              {label}
            </text>
          );
        })}

        {/* X-axis title - positioned below labels with proper spacing */}
        <text
          x={chartWidth / 2}
          y={chartHeight - padding.bottom + (hasRotatedLabels ? 55 : 40)}
          textAnchor="middle"
          fontSize="14"
          fill="#495057"
          fontWeight="600"
        >
          {metadata?.interval === 'hourly' ? 'Time (Hours)' : 
           metadata?.interval === 'daily' ? 'Date' :
           metadata?.interval === 'weekly' ? 'Week' :
           metadata?.interval === 'monthly' ? 'Month' : 'Time'}
        </text>

        {/* Vertical indicator line when hovering */}
        {tooltip.visible && tooltip.index >= 0 && tooltip.svgX && (
          <line
            x1={tooltip.svgX}
            y1={padding.top}
            x2={tooltip.svgX}
            y2={chartHeight - padding.bottom}
            stroke="#6c757d"
            strokeWidth="1.5"
            strokeDasharray="4,4"
            opacity="0.6"
          />
        )}

        {/* Chart lines based on chartType */}
        {chartType === 'watchTime' ? (
          <>
            {/* Watch Time line (Green) */}
            <path
              d={watchTimePath}
              fill="none"
              stroke="#28a745"
              strokeWidth="2.5"
            />
            {/* Watch Time data points with hover areas */}
            {data.map((point, index) => {
              const x = padding.left + (index / (data.length - 1 || 1)) * (chartWidth - padding.left - padding.right);
              const durationToUse = videoDuration || avgWatchDuration || 0;
              const watchTimeHours = ((point.views || 0) * durationToUse) / 3600;
              const watchTimeY = padding.top + scaleY(watchTimeHours, chartHeight - padding.top - padding.bottom);
              const isHovered = tooltip.visible && tooltip.index === index;
              return (
                <g key={index}>
                  {/* Invisible hover area for better tooltip interaction - extended upward for tooltip space */}
                  <rect
                    x={x - 30}
                    y={padding.top - 200}
                    width="60"
                    height={chartHeight - padding.top - padding.bottom + 300}
                    fill="transparent"
                    style={{ cursor: 'pointer', pointerEvents: 'auto' }}
                    onMouseMove={(e) => {
                      e.stopPropagation(); // Prevent chart-level handler from firing
                      e.preventDefault(); // Prevent default behavior
                      handleMouseMove(e, index);
                    }}
                    onMouseEnter={(e) => {
                      e.stopPropagation();
                      // Immediately set tooltip when entering hover area using the same handler
                      handleMouseMove(e as any, index);
                    }}
                  />
                  <circle 
                    cx={x} 
                    cy={watchTimeY} 
                    r={isHovered ? "6" : "4"} 
                    fill="#28a745" 
                    stroke="#fff" 
                    strokeWidth={isHovered ? "2" : "1"} 
                    style={{ cursor: 'pointer', transition: 'all 0.2s' }} 
                  />
                </g>
              );
            })}
          </>
        ) : (
          <>
            {/* Views line (Blue) */}
            <path
              d={viewsPath}
              fill="none"
              stroke="#0d6efd"
              strokeWidth="2.5"
            />
            {/* Likes line (Red) */}
            <path
              d={likesPath}
              fill="none"
              stroke="#dc3545"
              strokeWidth="2.5"
            />
            {/* Shares line (Green) */}
            <path
              d={sharesPath}
              fill="none"
              stroke="#28a745"
              strokeWidth="2.5"
            />
            {/* Saves line (Yellow/Orange) */}
            <path
              d={savesPath}
              fill="none"
              stroke="#ffc107"
              strokeWidth="2.5"
            />
            {/* Data points with hover areas */}
            {data.map((point, index) => {
              const divisor = data.length > 1 ? data.length - 1 : 1;
              const chartAreaWidth = chartWidth - padding.left - padding.right;
              const x = padding.left + (index / divisor) * chartAreaWidth;
              const viewsY = padding.top + scaleY(point.views || 0, chartHeight - padding.top - padding.bottom);
              const likesY = padding.top + scaleY(point.likes || 0, chartHeight - padding.top - padding.bottom);
              const sharesY = padding.top + scaleY(point.shares || 0, chartHeight - padding.top - padding.bottom);
              const savesY = padding.top + scaleY(point.saves || 0, chartHeight - padding.top - padding.bottom);
              const isHovered = tooltip.visible && tooltip.index === index;

              return (
                <g key={index}>
                  {/* Invisible hover area for better tooltip interaction - extended upward for tooltip space */}
                  <rect
                    x={x - 30}
                    y={padding.top - 200}
                    width="60"
                    height={chartHeight - padding.top - padding.bottom + 300}
                    fill="transparent"
                    style={{ cursor: 'pointer', pointerEvents: 'auto' }}
                    onMouseMove={(e) => {
                      e.stopPropagation(); // Prevent chart-level handler from firing
                      e.preventDefault(); // Prevent default behavior
                      handleMouseMove(e, index);
                    }}
                    onMouseEnter={(e) => {
                      e.stopPropagation();
                      // Immediately set tooltip when entering hover area using the same handler
                      handleMouseMove(e as any, index);
                    }}
                  />
                  {point.views > 0 && (
                    <circle 
                      cx={x} 
                      cy={viewsY} 
                      r={isHovered ? "6" : "4"} 
                      fill="#0d6efd" 
                      stroke="#fff" 
                      strokeWidth={isHovered ? "2" : "1"} 
                      style={{ cursor: 'pointer', transition: 'all 0.2s' }} 
                    />
                  )}
                  {point.likes > 0 && (
                    <circle 
                      cx={x} 
                      cy={likesY} 
                      r={isHovered ? "6" : "4"} 
                      fill="#dc3545" 
                      stroke="#fff" 
                      strokeWidth={isHovered ? "2" : "1"} 
                      style={{ cursor: 'pointer', transition: 'all 0.2s' }} 
                    />
                  )}
                  {point.shares > 0 && (
                    <circle 
                      cx={x} 
                      cy={sharesY} 
                      r={isHovered ? "6" : "4"} 
                      fill="#28a745" 
                      stroke="#fff" 
                      strokeWidth={isHovered ? "2" : "1"} 
                      style={{ cursor: 'pointer', transition: 'all 0.2s' }} 
                    />
                  )}
                  {point.saves > 0 && (
                    <circle 
                      cx={x} 
                      cy={savesY} 
                      r={isHovered ? "6" : "4"} 
                      fill="#ffc107" 
                      stroke="#fff" 
                      strokeWidth={isHovered ? "2" : "1"} 
                      style={{ cursor: 'pointer', transition: 'all 0.2s' }} 
                    />
                  )}
                </g>
              );
            })}
          </>
        )}
        </svg>
      </div>

      {/* Tooltip - positioned relative to SVG */}
      {tooltip.visible && tooltip.data && (() => {
        // Calculate tooltip position with better bounds checking
        const tooltipWidth = 200;
        const tooltipHeight = 180;
        const margin = 15;
        
        // Get container bounds
        const containerRect = containerRef.current?.getBoundingClientRect();
        if (!containerRect || !tooltip.data) return null;
        
        // Calculate optimal position
        let tooltipLeft = tooltip.x;
        let tooltipTop = tooltip.y;
        let transformX = '-50%';
        let transformY = 'calc(-100% - 20px)';
        let arrowBottom: string | undefined = '-6px';
        let arrowTop: string | undefined = undefined;
        
        // Adjust if tooltip would go off left edge
        if (tooltip.x - tooltipWidth / 2 < margin) {
          tooltipLeft = tooltipWidth / 2 + margin;
          transformX = '0%';
        }
        // Adjust if tooltip would go off right edge
        else if (tooltip.x + tooltipWidth / 2 > containerRect.width - margin) {
          tooltipLeft = containerRect.width - tooltipWidth / 2 - margin;
          transformX = '-100%';
        }
        
        // Adjust if tooltip would go off top edge - show below instead
        if (tooltip.y - tooltipHeight < margin) {
          tooltipTop = tooltip.y + 30;
          transformY = '20px';
          arrowBottom = undefined;
          arrowTop = '-6px';
        }
        
        const tooltipData = tooltip.data; // Store for null safety
        
        return (
          <div
            key={`tooltip-${tooltip.index}`}
            onMouseEnter={handleTooltipMouseEnter}
            onMouseLeave={handleTooltipMouseLeave}
            style={{
              position: 'absolute',
              left: `${tooltipLeft}px`,
              top: `${tooltipTop}px`,
              backgroundColor: '#ffffff',
              color: '#212529',
              padding: '12px 16px',
              borderRadius: '8px',
              fontSize: '13px',
              pointerEvents: 'auto',
              zIndex: 10000,
              boxShadow: '0 4px 12px rgba(0,0,0,0.15), 0 0 0 1px rgba(0,0,0,0.05)',
              transform: `translate(${transformX}, ${transformY})`,
              minWidth: '180px',
              maxWidth: '220px',
            }}
          >
            {/* Tooltip arrow */}
            <div
              style={{
                position: 'absolute',
                ...(arrowBottom !== undefined ? { bottom: arrowBottom } : {}),
                ...(arrowTop !== undefined ? { top: arrowTop } : {}),
                left: '50%',
                transform: 'translateX(-50%)',
                width: 0,
                height: 0,
                borderLeft: '6px solid transparent',
                borderRight: '6px solid transparent',
                borderTop: arrowTop !== undefined ? 'none' : '6px solid #ffffff',
                borderBottom: arrowBottom !== undefined ? 'none' : '6px solid #ffffff',
              }}
            />
          
          {/* Date/Time label */}
          <div style={{ 
            fontWeight: '600', 
            fontSize: '14px',
            marginBottom: '10px', 
            borderBottom: '1px solid #e9ecef', 
            paddingBottom: '8px',
            color: '#212529'
          }}>
            {tooltipData ? getTooltipLabel(tooltipData) : ''}
          </div>
          
          {/* Metrics */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            {chartType === 'watchTime' ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <div style={{ 
                  width: '10px', 
                  height: '10px', 
                  borderRadius: '50%', 
                  backgroundColor: '#28a745' 
                }}></div>
                <span style={{ color: '#6c757d', fontSize: '12px' }}>Watch Time:</span>
                  <span style={{ fontWeight: '600', color: '#28a745' }}>
                  {tooltipData ? ((tooltipData.views || 0) * (videoDuration || avgWatchDuration || 0) / 3600).toFixed(2) : '0'} hrs
                </span>
              </div>
            ) : (
              <>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{ 
                      width: '10px', 
                      height: '10px', 
                      borderRadius: '50%', 
                      backgroundColor: '#0d6efd' 
                    }}></div>
                    <span style={{ color: '#6c757d', fontSize: '12px' }}>Views:</span>
                  </div>
                  <span style={{ fontWeight: '600', color: '#0d6efd' }}>{tooltipData?.views || 0}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{ 
                      width: '10px', 
                      height: '10px', 
                      borderRadius: '50%', 
                      backgroundColor: '#dc3545' 
                    }}></div>
                    <span style={{ color: '#6c757d', fontSize: '12px' }}>Likes:</span>
                  </div>
                  <span style={{ fontWeight: '600', color: '#dc3545' }}>{tooltipData?.likes || 0}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{ 
                      width: '10px', 
                      height: '10px', 
                      borderRadius: '50%', 
                      backgroundColor: '#28a745' 
                    }}></div>
                    <span style={{ color: '#6c757d', fontSize: '12px' }}>Shares:</span>
                  </div>
                  <span style={{ fontWeight: '600', color: '#28a745' }}>{tooltipData?.shares || 0}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{ 
                      width: '10px', 
                      height: '10px', 
                      borderRadius: '50%', 
                      backgroundColor: '#ffc107' 
                    }}></div>
                    <span style={{ color: '#6c757d', fontSize: '12px' }}>Saves:</span>
                  </div>
                  <span style={{ fontWeight: '600', color: '#ffc107' }}>{tooltipData?.saves || 0}</span>
                </div>
              </>
            )}
          </div>
        </div>
        );
      })()}

      {/* Legend - Ensure it's at the bottom */}
      <div className="chart-legend d-flex justify-content-center gap-4 flex-wrap" style={{ paddingTop: hasRotatedLabels ? '30px' : '20px', marginTop: '0' }}>
        {chartType === 'watchTime' ? (
          <div className="d-flex align-items-center gap-2">
            <div style={{ width: '14px', height: '14px', backgroundColor: '#28a745', borderRadius: '50%', border: '2px solid #fff', boxShadow: '0 0 0 1px #28a745' }}></div>
            <span style={{ fontSize: '14px', color: '#495057', fontWeight: '500' }}>Watch Time</span>
          </div>
        ) : (
          <>
            <div className="d-flex align-items-center gap-2">
              <div style={{ width: '14px', height: '14px', backgroundColor: '#0d6efd', borderRadius: '50%', border: '2px solid #fff', boxShadow: '0 0 0 1px #0d6efd' }}></div>
              <span style={{ fontSize: '14px', color: '#495057', fontWeight: '500' }}>Views</span>
            </div>
            <div className="d-flex align-items-center gap-2">
              <div style={{ width: '14px', height: '14px', backgroundColor: '#dc3545', borderRadius: '50%', border: '2px solid #fff', boxShadow: '0 0 0 1px #dc3545' }}></div>
              <span style={{ fontSize: '14px', color: '#495057', fontWeight: '500' }}>Likes</span>
            </div>
            <div className="d-flex align-items-center gap-2">
              <div style={{ width: '14px', height: '14px', backgroundColor: '#28a745', borderRadius: '50%', border: '2px solid #fff', boxShadow: '0 0 0 1px #28a745' }}></div>
              <span style={{ fontSize: '14px', color: '#495057', fontWeight: '500' }}>Shares</span>
            </div>
            <div className="d-flex align-items-center gap-2">
              <div style={{ width: '14px', height: '14px', backgroundColor: '#ffc107', borderRadius: '50%', border: '2px solid #fff', boxShadow: '0 0 0 1px #ffc107' }}></div>
              <span style={{ fontSize: '14px', color: '#495057', fontWeight: '500' }}>Saves</span>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default SimpleLineChart;

